import { useEffect } from 'react'

type ElementType =
    | HTMLElement
    | HTMLButtonElement
    | HTMLDivElement
    | HTMLInputElement
    | HTMLFormElement
    | Element

type RefOrRefs =
    | React.RefObject<ElementType | null>
    | React.RefObject<ElementType | null>[]

interface UseClickOutsideOptions {
    /** Which events to listen for. Default: ['mousedown', 'touchstart'] */
    events?: string[]
    /** Listen in capture phase. Default: false */
    capture?: boolean
    /** Only attach listeners when enabled. Default: true */
    enabled?: boolean
}

/**
 * Perfect click-outside hook that handles single or multiple refs
 *
 * @param refs - Single ref or array of refs to monitor
 * @param callback - Function to call when click occurs outside all refs
 * @param options - Configuration options
 *
 * @example Single ref
 * const ref = useRef(null)
 * useClickOutside(ref, () => setOpen(false))
 *
 * @example Multiple refs (dropdown pattern)
 * const [buttonRef, panelRef] = [useRef(null), useRef(null)]
 * useClickOutside([buttonRef, panelRef], () => setOpen(false), { enabled: isOpen })
 *
 * @example Custom events
 * useClickOutside(ref, handler, { events: ['click'], capture: true })
 */
export function useClickOutside(
    refs: RefOrRefs,
    callback: () => void,
    options: UseClickOutsideOptions = {}
): void {
    const {
        events = ['mousedown', 'touchstart'],
        capture = false,
        enabled = true,
    } = options

    useEffect(() => {
        if (!enabled) return

        const handleEvent = (event: Event) => {
            const target = event.target as Node | null
            if (!target) return

            // Normalize to array for consistent handling
            const refArray = Array.isArray(refs) ? refs : [refs]

            // Check if click is inside any of the refs
            const isInsideAnyRef = refArray.some(
                (ref) => ref.current && ref.current.contains(target)
            )

            if (!isInsideAnyRef) {
                callback()
            }
        }

        // Add listeners for all specified events
        events.forEach((eventName) => {
            document.addEventListener(eventName, handleEvent, capture)
        })

        // Cleanup
        return () => {
            events.forEach((eventName) => {
                document.removeEventListener(eventName, handleEvent, capture)
            })
        }
    }, [refs, callback, events, capture, enabled])
}
